import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { createPortal } from "react-dom";

import { useUser } from "@components/user-context";
import {
  ActiveTab,
  useMMMActiveTab,
  useModelInsightsKPI,
  useModelInsightsKpiTimePeriod,
  useModelInsightsSorting,
} from "@features/mmm/hooks/mmm-url-state";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { FETCH_MODEL_INSIGHTS } from "@gql/mmm-model-insights";
import { Combobox } from "@headlessui/react";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchModelInsights,
  type FetchModelInsightsVariables,
} from "@north-beam/nb-client-side";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import {
  getMMMEventSelectionData,
  MMMTab,
} from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { TertiaryButton } from "@shared/buttons";
import { Select } from "@shared/selects";

import {
  type ModelMetadataDataColumn,
  type ModelMetadata,
} from "../explore-budgets/client-setting/types";
import { MINIMIZED_TOP_BAR_TAB_INFO_PORTAL_ID } from "../top-bars";

import { ChannelInsights } from "./channel-insights";
import { useModelMetadataKpis } from "./hooks/use-model-kpis";
import { ModelFilters } from "./model-filters";
import { ModelFitChart } from "./model-fit-chart";
import {
  curateHistoricalDataset,
  sortHistoricalDataset,
} from "./model-fit-chart/utils/export-to-csv";
import { ModelTitle } from "./model-title";
import { ModelTrainingDetailsModal } from "./model-training-details-modal";

interface Props {
  modelMetadata: ModelMetadata;
}

export type ChannelInsightsColumn = {
  column_name: string;
  breakdowns: { key: string; value: string }[];
  label: string;
  spend: number;
  kpi: number;
};

export const ModelInsightsContent = ({ modelMetadata }: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const [selectedKpi, setSelectedKpi] = useModelInsightsKPI();
  const [dateRange] = useModelInsightsKpiTimePeriod();
  const { adoptedKpiName } = useAdoptedBudgetMix();
  const { activeTab } = useMMMActiveTab();
  const { kpis } = useModelMetadataKpis();
  const { clientSettings } = useClientSettings();
  const { getKpi: getClientSettingsKpi } = useClientSettingData();
  const { user } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Logic to choose a default KPI
  useEffect(() => {
    // prioritise the adopted mix
    if (adoptedKpiName) {
      const kpi = kpis.find((k) => k.kpi_name === adoptedKpiName);

      setSelectedKpi(kpi);
    }
    // otherwise prioritise the first client settings
    else if (clientSettings.length) {
      const kpi = kpis.find(
        (k) => k.kpi_name === getClientSettingsKpi(clientSettings[0])?.kpi_name,
      );
      setSelectedKpi(kpi);
    } else if (modelMetadata) {
      setSelectedKpi(kpis[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelMetadata, setSelectedKpi, adoptedKpiName, kpis, clientSettings]);

  const { selectedCycle } = useSelectedCycle();

  const [sorting] = useModelInsightsSorting();

  const { data: costCurvesRawData, loading: costCurvesLoading } =
    useNorthbeamQuery<FetchModelInsights, FetchModelInsightsVariables>(
      FETCH_MODEL_INSIGHTS,
      { variables: { cycleId: selectedCycle.id } },
    );

  const costCurvesAllTimeData = useMemo(
    () => costCurvesRawData?.me.mmm.modelInsightsCostCurves?.all_time ?? {},
    [costCurvesRawData?.me.mmm.modelInsightsCostCurves],
  );

  const costCurvesAllTimeByQuantileData = useMemo(
    () =>
      costCurvesRawData?.me.mmm.modelInsightsCostCurvesByQuantile?.all_time ??
      {},
    [costCurvesRawData?.me.mmm.modelInsightsCostCurvesByQuantile],
  );

  const historicalContributionsData: Record<
    string,
    {
      spend: number;
      kpiData: Record<string, number>;
      promoData: Record<string, number>;
    }[]
  > = useMemo(
    () => costCurvesRawData?.me.mmm.modelInsightsHistoricalContributions ?? {},
    [costCurvesRawData?.me.mmm.modelInsightsHistoricalContributions],
  );

  const { columns, weeklySpendsByChannel } = useMemo(() => {
    const result: ChannelInsightsColumn[] = [];

    const spendsByChannel: Record<string, number[]> = {};
    const spendDataColumns = modelMetadata?.spend_data_columns ?? [];

    spendDataColumns.forEach((row: ModelMetadataDataColumn) => {
      const spendData = historicalContributionsData[row.column_name] ?? [];
      const curatedData = curateHistoricalDataset(spendData, dateRange);
      // use ALL Time for weekly spends
      spendsByChannel[row.column_name] = spendData.map((datum) => datum.spend);
      const spend = curatedData.reduce((acc: number, e) => acc + e.spend, 0);
      const kpi = curatedData.reduce((acc: number, e) => {
        if (selectedKpi) {
          return (
            acc +
            (e.kpiData?.[selectedKpi.kpi_name] || 0) +
            (e.promoData?.[selectedKpi.kpi_name] || 0)
          );
        }
        return acc;
      }, 0);

      result.push({
        column_name: row.column_name,
        breakdowns: row.mta_breakdowns ?? [],
        spend,
        kpi,
        label: row.friendly_name,
      });
    });

    return {
      columns: sortHistoricalDataset({
        columns: result,
        selectedKpi,
        sorting,
        user,
      }),
      weeklySpendsByChannel: spendsByChannel,
    };
  }, [
    modelMetadata?.spend_data_columns,
    sorting,
    user,
    selectedKpi,
    historicalContributionsData,
    dateRange,
  ]);

  if (
    !selectedKpi ||
    activeTab !== ActiveTab.MODEL_INSIGHTS ||
    !document.getElementById(MINIMIZED_TOP_BAR_TAB_INFO_PORTAL_ID)
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {createPortal(
        <div className="text-sm flex space-x-3">
          <div>
            <Select
              menuClassName="w-[250px]"
              value={selectedKpi.kpi_name}
              options={kpis.map((e) => ({
                label: e.friendly_name,
                value: e.kpi_name,
              }))}
              onChange={(value) => {
                trackMMMEvent({
                  component: ModelInsightsTrackedComponent.TOP_BAR_KPI_SELECTOR,
                  action: "onChange",
                  data: getMMMEventSelectionData(value),
                });

                const kpi = kpis.find((e) => e.kpi_name === value);
                if (kpi) {
                  setSelectedKpi(kpi);
                }
              }}
              buttonRenderer={({ open, displayValue }) => {
                return (
                  <Combobox.Button
                    as="button"
                    className={clsx(
                      "rounded-full px-4 flex items-center gap-1.5 h-8",
                      {
                        "bg-cosmic-4 hover:bg-cosmic-3": !open,
                        "bg-cosmic": open,
                      },
                    )}
                  >
                    <Combobox.Input value={displayValue} className="w-0" />
                    <span
                      className={clsx({
                        "text-cosmic": !open,
                        "text-white": open,
                      })}
                    >
                      KPI
                    </span>
                    <span
                      className={clsx("font-bold", {
                        "text-white": open,
                      })}
                    >
                      {displayValue}
                    </span>
                  </Combobox.Button>
                );
              }}
            />
          </div>
          <TertiaryButton
            className="text-sm bg-cosmic-4 hover:bg-cosmic-3"
            onClick={() => {
              trackMMMEvent({
                component:
                  ModelInsightsTrackedComponent.TOP_BAR_MODEL_TRAINING_DETAILS_BUTTON,
                action: "onClick",
                actionInfo: "Open model training details modal",
              });

              setIsModalOpen(true);
            }}
          >
            Model training details
          </TertiaryButton>
        </div>,
        document.getElementById(MINIMIZED_TOP_BAR_TAB_INFO_PORTAL_ID)!,
      )}
      <ModelFilters
        selectedKpi={selectedKpi}
        columns={columns}
        costCurvesRawData={costCurvesRawData}
        onChange={(kpi) => {
          trackMMMEvent({
            component: ModelInsightsTrackedComponent.KPI_SELECTOR,
            action: "onChange",
          });

          setSelectedKpi(kpi);
        }}
      />
      <div className="border-b border-v2-secondary" />
      <ModelTitle
        onClickModalOpen={() => {
          trackMMMEvent({
            component:
              ModelInsightsTrackedComponent.MODEL_TRAINING_DETAILS_BUTTON,
            action: "onClick",
            actionInfo: "Open model training details modal",
          });

          setIsModalOpen(true);
        }}
      />
      <ModelFitChart selectedKpi={selectedKpi} />
      <div className="border-b border-v2-secondary" />
      <ChannelInsights
        selectedKpi={selectedKpi}
        columns={columns}
        costCurvesAllTimeData={costCurvesAllTimeData}
        costCurvesAllTimeByQuantileData={costCurvesAllTimeByQuantileData}
        costCurvesLoading={costCurvesLoading}
        historicalContributionsData={historicalContributionsData}
        weeklySpendsByChannel={weeklySpendsByChannel}
      />
      <ModelTrainingDetailsModal
        isOpen={isModalOpen}
        onClose={() => {
          trackMMMEvent({
            component:
              ModelInsightsTrackedComponent.MODEL_TRAINING_DETAILS_MODAL,
            action: "onClose",
          });

          setIsModalOpen(false);
        }}
      />
    </div>
  );
};
